import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE_URL } from '@/lib/constants';
import { supabase } from '@/services/supabase';
import {
  ACCOUNT_TERMS_CONSENT_TEXT,
  PRIVACY_POLICY_VERSION,
  TERMS_VERSION,
} from '@/lib/privacy-consent';
import {
  buildSignupMetadata,
  buildSignupRedirectUrl,
  type SignupCaptureMetadata,
  type SignupResult,
} from '@/lib/auth-attribution';
import { trackFunnelEvent } from '@/lib/wc-funnel';
import type { SocialAuthProvider } from '@/lib/social-auth';
import type { AttributionSnapshot } from '@/lib/wc-funnel';

export interface User {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
  role: 'user' | 'admin';
  created_at: string;
  last_sign_in_at?: string;
}

export interface Session {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: User;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isReady: boolean; // true once initial getSession() resolves (even if null)
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setReady: () => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithOAuth: (provider: SocialAuthProvider, options?: OAuthOptions) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName?: string,
    options?: RegisterOptions
  ) => Promise<SignupResult>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<User, 'display_name' | 'avatar_url'>>) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

interface RegisterOptions {
  attribution?: AttributionSnapshot | null;
  redirectTo?: string;
  capture?: SignupCaptureMetadata;
}

interface OAuthOptions {
  attribution?: AttributionSnapshot | null;
  redirectTo?: string;
  mode?: 'signin' | 'signup';
  ageConfirmed?: boolean;
}

interface PendingOAuthContext {
  provider: SocialAuthProvider;
  mode: 'signin' | 'signup';
  redirectTo?: string;
  sourceUrl?: string;
  attribution?: AttributionSnapshot | null;
  ageConfirmed?: boolean;
  startedAt: string;
}

const PENDING_OAUTH_CONTEXT_KEY = 'bb_pending_oauth_context_v1';
const PENDING_OAUTH_MAX_AGE_MS = 60 * 60 * 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isLoading: false,
      isReady: false,
      isAuthenticated: false,

      setUser: user => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      setSession: session => {
        set({
          session,
          user: session?.user || null,
          isAuthenticated: !!session?.user,
        });
      },

      setLoading: isLoading => {
        set({ isLoading });
      },

      setReady: () => {
        set({ isReady: true });
      },

      login: async (email: string, password: string) => {
        const { setSession, setLoading } = get();

        try {
          setLoading(true);

          // Timeout wrapper to prevent Supabase auth from hanging indefinitely
          const authResult = await Promise.race([
            supabase.auth.signInWithPassword({ email, password }),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Login timed out. Please try again.')), 10000)
            ),
          ]);

          const { data, error } = authResult;

          if (error) throw error;

          if (data.session && data.user) {
            const userProfile = await fetchUserProfile(data.user.id);

            const session: Session = {
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
              expires_at: data.session.expires_at || 0,
              user: {
                id: data.user.id,
                email: data.user.email!,
                display_name: userProfile?.display_name,
                avatar_url: userProfile?.avatar_url,
                role: userProfile?.role || 'user',
                created_at: data.user.created_at,
                last_sign_in_at: data.user.last_sign_in_at,
              },
            };

            setSession(session);
          }
        } catch (error) {
          console.error('Login error:', error);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      loginWithOAuth: async (provider: SocialAuthProvider, options?: OAuthOptions) => {
        const { setLoading } = get();

        try {
          setLoading(true);
          storePendingOAuthContext({
            provider,
            mode: options?.mode || 'signin',
            redirectTo: options?.redirectTo,
            sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
            attribution: options?.attribution || null,
            ageConfirmed: options?.ageConfirmed,
            startedAt: new Date().toISOString(),
          });

          const { error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
              redirectTo: buildSignupRedirectUrl(
                window.location.origin,
                options?.redirectTo,
                options?.attribution
              ),
            },
          });

          if (error) throw error;
        } catch (error) {
          clearPendingOAuthContext();
          console.error('OAuth login error:', error);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      register: async (
        email: string,
        password: string,
        displayName?: string,
        options?: RegisterOptions
      ) => {
        const { setSession, setLoading } = get();

        try {
          setLoading(true);

          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: buildSignupMetadata(displayName, options?.attribution, options?.capture),
              emailRedirectTo: buildSignupRedirectUrl(
                window.location.origin,
                options?.redirectTo,
                options?.attribution
              ),
            },
          });

          if (error) throw error;

          // Supabase rate limit: returns null user + null session + no error
          if (!data.user && !data.session) {
            throw new Error(
              'Signup is temporarily unavailable. Please try again in a few minutes.'
            );
          }

          // Supabase returns a user with empty identities if email already exists
          // (security: doesn't reveal whether email is registered)
          if (data.user && data.user.identities && data.user.identities.length === 0) {
            throw new Error('ACCOUNT_EXISTS');
          }

          // If no session, email confirmation is required
          if (!data.session && data.user) {
            // User created but needs to confirm email
            return { confirmationRequired: true };
          }

          if (data.session && data.user) {
            // TODO: Create user profile when backend supports it
            // await createUserProfile(data.user.id, { email: data.user.email!, display_name: displayName })

            const session: Session = {
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
              expires_at: data.session.expires_at || 0,
              user: {
                id: data.user.id,
                email: data.user.email!,
                display_name: displayName,
                avatar_url: undefined,
                role: 'user',
                created_at: data.user.created_at,
                last_sign_in_at: data.user.last_sign_in_at,
              },
            };

            setSession(session);
          }

          return { confirmationRequired: false };
        } catch (error) {
          console.error('Registration error:', error);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      logout: async () => {
        const { setSession, setLoading } = get();

        try {
          setLoading(true);

          const { error } = await supabase.auth.signOut();

          if (error) throw error;

          setSession(null);
        } catch (error) {
          console.error('Logout error:', error);
          // Force logout even if API call fails
          setSession(null);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      refreshSession: async () => {
        const { setSession, setLoading } = get();

        try {
          setLoading(true);

          const { data, error } = await supabase.auth.refreshSession();

          if (error) throw error;

          if (data.session && data.user) {
            const userProfile = await fetchUserProfile(data.user.id);

            const session: Session = {
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
              expires_at: data.session.expires_at || 0,
              user: {
                id: data.user.id,
                email: data.user.email!,
                display_name: userProfile?.display_name,
                avatar_url: userProfile?.avatar_url,
                role: userProfile?.role || 'user',
                created_at: data.user.created_at,
                last_sign_in_at: data.user.last_sign_in_at,
              },
            };

            setSession(session);
          } else {
            setSession(null);
          }
        } catch (error) {
          console.error('Session refresh error:', error);
          setSession(null);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      updateProfile: async updates => {
        const { user, setUser, setLoading } = get();

        if (!user) {
          throw new Error('No user logged in');
        }

        try {
          setLoading(true);

          const { error } = await supabase.from('user_profiles').update(updates).eq('id', user.id);

          if (error) throw error;

          // Update local user state
          setUser({
            ...user,
            ...updates,
          });
        } catch (error) {
          console.error('Profile update error:', error);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      resetPassword: async (email: string) => {
        try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
          });

          if (error) throw error;
        } catch (error) {
          console.error('Password reset error:', error);
          throw error;
        }
      },

      updatePassword: async (password: string) => {
        try {
          const { error } = await supabase.auth.updateUser({
            password,
          });

          if (error) throw error;
        } catch (error) {
          console.error('Password update error:', error);
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        session: state.session,
      }),
    }
  )
);

// Helper functions
async function fetchUserProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      // Table may not exist yet — fail silently
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function storePendingOAuthContext(context: PendingOAuthContext) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PENDING_OAUTH_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Best-effort only. OAuth itself should not fail because local storage is unavailable.
  }
}

function clearPendingOAuthContext() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PENDING_OAUTH_CONTEXT_KEY);
  } catch {
    // Best-effort only.
  }
}

function readPendingOAuthContext(): PendingOAuthContext | null {
  if (typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(PENDING_OAUTH_CONTEXT_KEY);
    if (!raw) return null;

    const context = JSON.parse(raw) as PendingOAuthContext;
    const startedAt = Date.parse(context.startedAt);
    if (!Number.isFinite(startedAt) || Date.now() - startedAt > PENDING_OAUTH_MAX_AGE_MS) {
      clearPendingOAuthContext();
      return null;
    }

    return context;
  } catch {
    clearPendingOAuthContext();
    return null;
  }
}

function bestOAuthTouch(context: PendingOAuthContext) {
  return context.attribution?.latestTouch || context.attribution?.firstTouch || null;
}

async function syncPendingOAuthContext(session: Session) {
  const context = readPendingOAuthContext();
  if (!context) return;

  const touch = bestOAuthTouch(context);
  const isSignup = context.mode === 'signup';
  const sourceUrl =
    context.sourceUrl ||
    touch?.url ||
    (typeof window !== 'undefined' ? window.location.href : undefined);

  try {
    const response = await fetch(`${API_BASE_URL}/privacy/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        policyVersion: PRIVACY_POLICY_VERSION,
        termsVersion: TERMS_VERSION,
        source: touch?.source || undefined,
        campaign: touch?.campaign || undefined,
        sourceUrl,
        ...(isSignup
          ? {
              accountTermsAccepted: true,
              ageConfirmed: Boolean(context.ageConfirmed),
              consentText: ACCOUNT_TERMS_CONSENT_TEXT,
            }
          : {}),
      }),
    });

    if (!response.ok) throw new Error(`privacy preferences ${response.status}`);
    clearPendingOAuthContext();
  } catch (error) {
    console.warn('OAuth attribution sync failed:', error);
    return;
  }

  void trackFunnelEvent(isSignup ? 'signup_created' : 'signin_completed', {
    provider: context.provider,
    method: 'oauth',
    redirect_to: context.redirectTo,
  });
  void trackFunnelEvent('social_auth_completed', {
    provider: context.provider,
    mode: context.mode,
    redirect_to: context.redirectTo,
  });
}

// Initialize auth state on app load
supabase.auth.onAuthStateChange(async (event, session) => {
  const store = useAuthStore.getState();

  if (event === 'SIGNED_IN' && session) {
    const userProfile = await fetchUserProfile(session.user.id);

    const authSession: Session = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: session.expires_at || 0,
      user: {
        id: session.user.id,
        email: session.user.email!,
        display_name: userProfile?.display_name,
        avatar_url: userProfile?.avatar_url,
        role: userProfile?.role || 'user',
        created_at: session.user.created_at,
        last_sign_in_at: session.user.last_sign_in_at,
      },
    };

    store.setSession(authSession);
    void syncPendingOAuthContext(authSession);
  } else if (event === 'SIGNED_OUT') {
    store.setSession(null);
  } else if (event === 'TOKEN_REFRESHED' && session) {
    const userProfile = await fetchUserProfile(session.user.id);

    const authSession: Session = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: session.expires_at || 0,
      user: {
        id: session.user.id,
        email: session.user.email!,
        display_name: userProfile?.display_name,
        avatar_url: userProfile?.avatar_url,
        role: userProfile?.role || 'user',
        created_at: session.user.created_at,
        last_sign_in_at: session.user.last_sign_in_at,
      },
    };

    store.setSession(authSession);
  }
});
